import { clientLogger } from '@/lib/clientLogger';
import { CapacitorHttp } from '@capacitor/core';
import {
  AppUpdateAvailability,
  CapacitorUpdater,
  type AppUpdateInfo,
} from '@capgo/capacitor-updater';
import { compareSemver } from './appVersion';
import {
  getAppOrigin,
  getStorageItem,
  isAndroid,
  isIOS,
  isNative,
  removeStorageItem,
  setStorageItem,
} from './capacitor';

const IOS_BUNDLE_ID = 'com.servicestrike.ios';
const IOS_APP_STORE_ID = '6797074179';
const ANDROID_PACKAGE_NAME = 'com.servicestrike.app';
const APP_STORE_COUNTRY = 'US';
const FALLBACK_PUBLIC_URL = 'https://app.servicestrike.com';
const CHECK_TIMEOUT_MS = 5_000;
const OPTIONAL_UPDATE_SNOOZE_KEY = 'native_update_optional_snooze';
export const OPTIONAL_UPDATE_SNOOZE_MS = 30 * 60 * 1_000;

export type NativeUpdatePolicy = {
  schemaVersion: 1;
  ios: {
    minimumVersionName: string | null;
  };
  android: {
    minimumVersionCode: number | null;
  };
};

export type NativeUpdateDecision =
  | { kind: 'none' }
  | {
      kind: 'optional' | 'required';
      platform: 'android' | 'ios';
      currentVersion: string;
      availableVersion: string;
    };

export type NativeUpdateCheckOutcome = {
  decision: NativeUpdateDecision;
  storeStatus: 'available' | 'current' | 'unavailable';
  policyStatus: 'available' | 'unavailable';
};

let inflightCheck: Promise<NativeUpdateCheckOutcome> | null = null;
let memorySnooze: NativeUpdateSnooze | null = null;

type NativeUpdateSnooze = {
  platform: 'android' | 'ios';
  availableVersion: string;
  expiresAt: number;
};

function isIosVersion(value: unknown): value is string {
  return typeof value === 'string' && /^\d+(?:\.\d+){1,2}$/.test(value);
}

function isPolicy(value: unknown): value is NativeUpdatePolicy {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const policy = value as Record<string, unknown>;
  const ios = policy.ios as Record<string, unknown> | undefined;
  const android = policy.android as Record<string, unknown> | undefined;
  const iosMinimum = ios?.minimumVersionName;
  const androidMinimum = android?.minimumVersionCode;

  return (
    policy.schemaVersion === 1 &&
    !!ios &&
    !!android &&
    (iosMinimum === null || isIosVersion(iosMinimum)) &&
    (androidMinimum === null ||
      (typeof androidMinimum === 'number' &&
        Number.isInteger(androidMinimum) &&
        androidMinimum > 0))
  );
}

function getPolicyUrl(): string {
  const configuredUrl = import.meta.env.VITE_NATIVE_UPDATE_POLICY_URL?.trim();
  if (configuredUrl) {
    return configuredUrl;
  }

  const publicOrigin = (getAppOrigin() || FALLBACK_PUBLIC_URL).replace(/\/+$/, '');
  const environment = import.meta.env.MODE.endsWith('debug') ? 'debug' : 'prod';
  return `${publicOrigin}/ota/native-update-policy/${environment}.json`;
}

async function withTimeout<T>(promise: Promise<T>): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Native update check timed out')), CHECK_TIMEOUT_MS);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

async function fetchPolicy(): Promise<NativeUpdatePolicy> {
  const url = `${getPolicyUrl()}?t=${Date.now()}`;
  const response = await CapacitorHttp.get({
    url,
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
    },
    readTimeout: CHECK_TIMEOUT_MS,
    connectTimeout: CHECK_TIMEOUT_MS,
    disableRedirects: false,
  });

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Native update policy returned status ${response.status}`);
  }

  const data: unknown =
    typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

  if (!isPolicy(data)) {
    throw new Error('Native update policy payload is invalid');
  }

  return data;
}

async function fetchStoreInfo(): Promise<AppUpdateInfo> {
  if (isIOS()) {
    // capacitor.config.ts uses Android's package name globally. Correct the updater's
    // runtime identifier before it performs Apple's bundle-id lookup.
    // Requires plugins.CapacitorUpdater.allowModifyAppId in the synced native config.
    await CapacitorUpdater.setAppId({ appId: IOS_BUNDLE_ID });
    return CapacitorUpdater.getAppUpdateInfo({ country: APP_STORE_COUNTRY });
  }

  return CapacitorUpdater.getAppUpdateInfo();
}

function isStoreUpdateAvailable(info: AppUpdateInfo): boolean {
  return (
    info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE ||
    info.updateAvailability === AppUpdateAvailability.UPDATE_IN_PROGRESS
  );
}

function decideUpdate(
  info: AppUpdateInfo,
  policy: NativeUpdatePolicy | null,
): NativeUpdateDecision {
  if (!isStoreUpdateAvailable(info)) {
    return { kind: 'none' };
  }

  if (isAndroid()) {
    const currentVersion = Number.parseInt(info.currentVersionCode, 10);
    const availableVersion = Number.parseInt(info.availableVersionCode ?? '', 10);
    if (Number.isNaN(currentVersion) || Number.isNaN(availableVersion)) {
      return { kind: 'none' };
    }

    const minimumVersion = policy?.android.minimumVersionCode ?? null;
    const required =
      minimumVersion !== null &&
      currentVersion < minimumVersion &&
      availableVersion >= minimumVersion;

    return {
      kind: required ? 'required' : 'optional',
      platform: 'android',
      currentVersion: String(currentVersion),
      availableVersion: String(availableVersion),
    };
  }

  const currentVersion = info.currentVersionName;
  const availableVersion = info.availableVersionName;
  if (!availableVersion || !isIosVersion(currentVersion) || !isIosVersion(availableVersion)) {
    return { kind: 'none' };
  }
  if (compareSemver(currentVersion, availableVersion) >= 0) {
    return { kind: 'none' };
  }

  const minimumVersion = policy?.ios.minimumVersionName ?? null;
  const required =
    minimumVersion !== null &&
    compareSemver(currentVersion, minimumVersion) < 0 &&
    compareSemver(availableVersion, minimumVersion) >= 0;

  return {
    kind: required ? 'required' : 'optional',
    platform: 'ios',
    currentVersion,
    availableVersion,
  };
}

async function runCheck(): Promise<NativeUpdateCheckOutcome> {
  if (!isNative() || (!isAndroid() && !isIOS())) {
    return {
      decision: { kind: 'none' },
      storeStatus: 'unavailable',
      policyStatus: 'unavailable',
    };
  }

  const [storeResult, policyResult] = await Promise.allSettled([
    withTimeout(fetchStoreInfo()),
    withTimeout(fetchPolicy()),
  ]);

  if (policyResult.status === 'rejected') {
    clientLogger.warn('[Native Update] Policy check failed; mandatory updates fail open:', policyResult.reason);
  }

  if (storeResult.status === 'rejected') {
    clientLogger.warn('[Native Update] Store check failed; skipping native update prompt:', storeResult.reason);
    return {
      decision: { kind: 'none' },
      storeStatus: 'unavailable',
      policyStatus: policyResult.status === 'fulfilled' ? 'available' : 'unavailable',
    };
  }

  const storeInfo = storeResult.value;
  if (storeInfo.updateAvailability === AppUpdateAvailability.UPDATE_NOT_AVAILABLE) {
    return {
      decision: { kind: 'none' },
      storeStatus: 'current',
      policyStatus: policyResult.status === 'fulfilled' ? 'available' : 'unavailable',
    };
  }
  if (!isStoreUpdateAvailable(storeInfo)) {
    clientLogger.warn(
      '[Native Update] Store availability is unknown; skipping native update prompt:',
      storeInfo.updateAvailability,
    );
    return {
      decision: { kind: 'none' },
      storeStatus: 'unavailable',
      policyStatus: policyResult.status === 'fulfilled' ? 'available' : 'unavailable',
    };
  }

  const policy = policyResult.status === 'fulfilled' ? policyResult.value : null;
  return {
    decision: decideUpdate(storeInfo, policy),
    storeStatus: 'available',
    policyStatus: policy ? 'available' : 'unavailable',
  };
}

export function checkNativeAppUpdate(): Promise<NativeUpdateCheckOutcome> {
  if (inflightCheck) {
    return inflightCheck;
  }

  inflightCheck = runCheck().finally(() => {
    inflightCheck = null;
  });

  return inflightCheck;
}

function isMatchingActiveSnooze(
  snooze: NativeUpdateSnooze,
  decision: Exclude<NativeUpdateDecision, { kind: 'none' }>,
): boolean {
  return (
    decision.kind === 'optional' &&
    snooze.platform === decision.platform &&
    snooze.availableVersion === decision.availableVersion &&
    snooze.expiresAt > Date.now()
  );
}

function parseSnooze(value: string | null): NativeUpdateSnooze | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<NativeUpdateSnooze>;
    if (
      (parsed.platform === 'android' || parsed.platform === 'ios') &&
      typeof parsed.availableVersion === 'string' &&
      typeof parsed.expiresAt === 'number' &&
      Number.isFinite(parsed.expiresAt)
    ) {
      return parsed as NativeUpdateSnooze;
    }
  } catch {
    // Invalid persisted state is treated as no snooze and cleaned up below.
  }

  return null;
}

export async function isNativeAppUpdateSnoozed(
  decision: Exclude<NativeUpdateDecision, { kind: 'none' }>,
): Promise<boolean> {
  if (decision.kind === 'required') {
    return false;
  }

  if (memorySnooze && isMatchingActiveSnooze(memorySnooze, decision)) {
    return true;
  }

  try {
    const persisted = parseSnooze(await getStorageItem(OPTIONAL_UPDATE_SNOOZE_KEY));
    if (persisted && isMatchingActiveSnooze(persisted, decision)) {
      memorySnooze = persisted;
      return true;
    }

    memorySnooze = null;
    await removeStorageItem(OPTIONAL_UPDATE_SNOOZE_KEY);
  } catch (error) {
    clientLogger.warn('[Native Update] Could not read optional update snooze:', error);
  }

  return false;
}

export async function snoozeNativeAppUpdate(
  decision: Exclude<NativeUpdateDecision, { kind: 'none' }>,
): Promise<void> {
  if (decision.kind === 'required') {
    return;
  }

  const snooze: NativeUpdateSnooze = {
    platform: decision.platform,
    availableVersion: decision.availableVersion,
    expiresAt: Date.now() + OPTIONAL_UPDATE_SNOOZE_MS,
  };
  memorySnooze = snooze;

  try {
    await setStorageItem(OPTIONAL_UPDATE_SNOOZE_KEY, JSON.stringify(snooze));
  } catch (error) {
    // The in-memory value still prevents repeat prompts for this app process.
    clientLogger.warn('[Native Update] Could not persist optional update snooze:', error);
  }
}

export async function openNativeAppStore(): Promise<void> {
  if (isIOS()) {
    await CapacitorUpdater.openAppStore({ appId: IOS_APP_STORE_ID });
    return;
  }

  await CapacitorUpdater.openAppStore({ packageName: ANDROID_PACKAGE_NAME });
}
